/**
 * Daily quote — one quote per calendar day, from the dwyl/quotes dataset.
 *
 *   1. exactly one quote per day
 *   2. never the same quote two days running
 *   3. survive a reload, a flaky network, and an offline start
 *
 * A plain "GET a random quote on mount" satisfies (2) badly and (1) and (3)
 * not at all: every reload is a new quote, the network is on the critical
 * path, and the text swaps under the reader mid-sentence. So the module splits
 * the work in two:
 *
 *   - `resolveDailyQuote()` is **synchronous** and answers "what is today's
 *     quote?" from a per-day pin in localStorage. It never awaits, so once
 *     the pool has been cached the line paints on the first frame.
 *   - `refreshQuotePool()` is **asynchronous** and only fills the *pool* of
 *     candidates for future days.
 *
 * The pool is the [dwyl/quotes](https://github.com/dwyl/quotes) dataset — a
 * single static JSON file on GitHub raw, and the only source:
 *
 *   - `access-control-allow-origin: *`, so it is readable from the browser
 *     with no proxy and no edge function
 *   - 1,655 quotes, 63KB gzipped, served from GitHub's CDN
 *   - fields are already `text` and `author` — no normalization
 *   - static and versioned, so it never rate-limits and never changes shape
 *
 * The pool is shuffled and a 400-quote window is cached in localStorage
 * (~45KB), refreshed monthly. At one quote a day that is a ~14-month buffer,
 * so the refill is about freshness, not supply.
 *
 * ## The cost of a single source
 *
 * There is no offline fallback. On a device that has never loaded the page
 * before, the pool is empty until the file arrives, so `resolveDailyQuote()`
 * returns null and `useDailyQuote()` reports `isLoading`. The caller is
 * expected to reserve the space (see `DailyQuote`) so the hero does not jump
 * when the quote lands. After the first successful load the pool is cached
 * permanently and this state never recurs — it is a first-run-only case, not
 * an ongoing one.
 */

export interface Quote {
  text: string;
  author: string;
}

export interface DailyQuoteResult {
  quote: Quote;
  /** Local `YYYY-MM-DD` the quote is pinned to. */
  dayKey: string;
}

const STORAGE_KEY = "lyder-talk:daily-quote:v1";

/** Keep only this many recently-shown quotes around for de-duplication. */
const USED_HISTORY_LIMIT = 150;

/** Refill once the pool runs this low, so there is never a dry day. */
const POOL_LOW_WATERMARK = 14;

/**
 * Refresh on a monthly cycle so newly added quotes get picked up. The pool is
 * ~4 years deep, so this is about freshness, not supply.
 */
const POOL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * How many of the fetched quotes to keep. The file ships 1,655; holding a
 * 400-quote window costs ~45KB of localStorage and still leaves a ~14-month
 * buffer at one quote a day.
 */
const POOL_KEEP = 400;

const REQUEST_TIMEOUT_MS = 10_000;

/** Static JSON on GitHub raw: CORS `*`, CDN-cached, versioned. */
const QUOTES_URL =
  "https://raw.githubusercontent.com/dwyl/quotes/main/quotes.json";

/* ── Dates ────────────────────────────────────────────────────────────
   Deliberately local, not UTC: a daily quote should turn over at *your*
   midnight, and the pin is per-browser anyway so there is no shared
   "everyone must agree" constraint. */

/** `YYYY-MM-DD` in the reader's own timezone. */
export function getDayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Split a quote onto two lines at its first semicolon.
 *
 * A short quotation almost always turns at a semicolon — "Try not to become a
 * man of success; rather become a man of value." is two beats, and setting it
 * as one run-on line buries the turn. Breaking there gives the quote a shape
 * instead of a blob.
 *
 * Returns a single-element array when there is no semicolon, so callers can
 * always map over the result.
 */
export function splitQuoteForDisplay(text: string): string[] {
  const at = text.indexOf(";");
  if (at === -1) return [text];

  const head = text.slice(0, at).trim();
  const tail = text.slice(at + 1).trim();

  // A semicolon at either extreme ("; Start here.") isn't a turn, it's a typo
  // or a different kind of punctuation — don't manufacture a line break.
  if (!head || !tail) return [text];

  return [head, tail];
}

/** Ms from `now` until the next local midnight. */
export function msUntilNextDay(now: Date = new Date()): number {
  const next = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
  ).getTime();
  return Math.max(1000, next - now.getTime());
}

/* ── Storage ─────────────────────────────────────────────────────────
   One key holding the pin, the unspent pool, and the used-history.
   Every access is guarded: Safari private mode and some embedded
   webviews throw on `localStorage`, and a decorative quote is never worth
   a thrown render. */

/** A pinned quote. `source` is tolerated from older stores but never written. */
interface StoredQuote extends Quote {
  source?: "api" | "bundled";
}

interface QuoteStore {
  /**
   * v2 discards stores written by v1, which could hold duplicate entries —
   * see the note on `refreshQuotePool`. The pool refetches on its own, so
   * resetting costs one download.
   */
  version: 2;
  today: (StoredQuote & { dayKey: string }) | null;
  pool: Quote[];
  used: string[];
  poolFetchedAt: number | null;
}

const emptyStore = (): QuoteStore => ({
  version: 2,
  today: null,
  pool: [],
  used: [],
  poolFetchedAt: null,
});

function readStore(): QuoteStore {
  if (typeof localStorage === "undefined") return emptyStore();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as Partial<QuoteStore>;
    if (parsed?.version !== 2) return emptyStore();
    return {
      version: 2,
      today: parsed.today ?? null,
      pool: Array.isArray(parsed.pool) ? parsed.pool : [],
      used: Array.isArray(parsed.used) ? parsed.used : [],
      poolFetchedAt:
        typeof parsed.poolFetchedAt === "number" ? parsed.poolFetchedAt : null,
    };
  } catch (error) {
    console.warn("[daily-quote] unreadable store, starting fresh", error);
    return emptyStore();
  }
}

function writeStore(store: QuoteStore): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    // Quota or private mode. The quote still renders for this session; only
    // persistence across reloads is lost.
    console.warn("[daily-quote] could not persist quote", error);
  }
}

/** Test seam. */
export function clearDailyQuoteStore(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

/* ── Picking ─────────────────────────────────────────────────────────── */

/** Normalise for the used-history key so trivial reformatting still matches. */
const fingerprint = (quote: Quote) =>
  `${quote.text}::${quote.author}`.toLowerCase().replace(/\s+/g, " ").trim();

/**
 * Take the next unspent quote, or null if there is nothing to take.
 *
 * Null is a real state, not an error: with a single source, a device that has
 * never fetched the dataset has no pool and no fallback. The caller renders
 * reserved space until `refreshQuotePool()` lands.
 */
function takeNextQuote(store: QuoteStore): StoredQuote | null {
  while (store.pool.length > 0) {
    const candidate = store.pool.shift();
    if (!candidate) break;
    if (!store.used.includes(fingerprint(candidate))) {
      return { ...candidate };
    }
  }
  return null;
}

/**
 * Synchronously resolve the quote for `date` and pin it.
 *
 * Idempotent within a day: the second call on the same calendar day returns
 * the stored quote untouched, which is what keeps a reload from re-rolling.
 *
 * Returns null when the pool is empty — the first run before the dataset has
 * been fetched. Nothing is written in that case, so the next call retries
 * once the pool exists.
 */
export function resolveDailyQuote(date: Date = new Date()): DailyQuoteResult | null {
  const dayKey = getDayKey(date);
  const store = readStore();

  if (store.today?.dayKey === dayKey) {
    const { dayKey: _dayKey, ...stored } = store.today;
    return { quote: { text: stored.text, author: stored.author }, dayKey };
  }

  const picked = takeNextQuote(store);
  if (!picked) return null;

  // New day (or first ever visit). Roll the used-history forward and pin.
  const used = [fingerprint(picked), ...store.used].slice(0, USED_HISTORY_LIMIT);

  writeStore({
    ...store,
    used,
    today: { ...picked, dayKey },
  });

  return { quote: { text: picked.text, author: picked.author }, dayKey };
}

/* ── Refill ──────────────────────────────────────────────────────────── */

/** dwyl/quotes ships `{ author, text }` — already our `Quote` shape. */
interface RawQuote {
  text?: string;
  author?: string;
}

/** Fisher–Yates. Keeps the window varied across refills. */
function shuffle<T>(items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Fetch the whole dataset and return a shuffled working window.
 *
 * Static JSON on GitHub raw, so there is no key, no rate limit, no shape
 * churn, and CORS `*` means the browser can read it directly — no edge
 * function and nothing to deploy.
 *
 * Exported for tests.
 */
export async function fetchQuotePool(signal?: AbortSignal): Promise<Quote[]> {
  const response = await fetch(QUOTES_URL, {
    signal,
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`daily-quote: HTTP ${response.status}`);
  }

  const payload = (await response.json()) as RawQuote[] | null;
  if (!Array.isArray(payload)) {
    throw new Error("daily-quote: expected a JSON array");
  }

  const seen = new Set<string>();
  const quotes: Quote[] = [];

  for (const entry of payload) {
    const text = entry?.text?.replace(/\s+/g, " ").trim();
    const author = entry?.author?.replace(/\s+/g, " ").trim();
    if (!text || !author) continue;

    // The dataset carries a few duplicates.
    const key = fingerprint({ text, author });
    if (seen.has(key)) continue;
    seen.add(key);

    quotes.push({ text, author });
  }

  return shuffle(quotes).slice(0, POOL_KEEP);
}

function poolNeedsRefill(store: QuoteStore, now: number): boolean {
  if (store.pool.length < POOL_LOW_WATERMARK) return true;
  if (
    store.poolFetchedAt !== null &&
    now - store.poolFetchedAt > POOL_MAX_AGE_MS
  ) {
    return true;
  }
  return false;
}

/**
 * Top up the candidate pool for *future* days.
 *
 * Never touches today's pin — by the time this resolves the reader is already
 * looking at a quote, and swapping it out would be a visible lurch. Safe to
 * call on every mount; it no-ops when the pool is healthy.
 *
 * Returns the number of quotes added, or 0 if it skipped or failed.
 */
export async function refreshQuotePool(
  now: number = Date.now(),
): Promise<number> {
  const store = readStore();
  if (!poolNeedsRefill(store, now)) return 0;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const batch = await fetchQuotePool(controller.signal);

    const latest = readStore();
    // Seed with both the used-history and what is already queued. `used` alone
    // is not enough: React StrictMode double-invokes effects, so two refills
    // can race and both merge into an empty pool. Without the pool's own
    // fingerprints those duplicates survive, and a quote can then be served
    // twice on different days.
    const seen = new Set<string>(latest.used);
    for (const queued of latest.pool) seen.add(fingerprint(queued));
    const additions: Quote[] = [];
    for (const quote of batch) {
      const key = fingerprint(quote);
      if (seen.has(key)) continue;
      seen.add(key);
      additions.push(quote);
    }

    if (additions.length === 0) return 0;

    writeStore({
      ...latest,
      pool: [...latest.pool, ...additions],
      poolFetchedAt: now,
    });
    return additions.length;
  } catch (error) {
    // Offline, blocked, or GitHub unreachable. The quote simply stays absent
    // until a later visit succeeds — never surface this.
    console.warn("[daily-quote] pool refresh failed", error);
    return 0;
  } finally {
    clearTimeout(timer);
  }
}

